import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShoppingBag, Trash2, Package, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import BackButton from '../components/ui/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShopCartPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [cart, setCart] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    full_name: '', email: '', phone: '', address: '', city: '', province: '', postal_code: '', country: 'Canada',
  });

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('shop_cart') || '[]');
    setCart(stored);
    if (stored.length > 0) calculateTotals(stored);
    else setLoading(false);
  }, []);

  const calculateTotals = async (items) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/shop/cart/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })) }),
      });
      const data = await res.json();
      setTotals(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const updateQty = (productId, delta) => {
    const updated = cart.map((i) =>
      i.product_id === productId ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i
    );
    setCart(updated);
    localStorage.setItem('shop_cart', JSON.stringify(updated));
    calculateTotals(updated);
  };

  const removeItem = (productId) => {
    const updated = cart.filter((i) => i.product_id !== productId);
    setCart(updated);
    localStorage.setItem('shop_cart', JSON.stringify(updated));
    if (updated.length > 0) calculateTotals(updated);
    else { setTotals(null); setLoading(false); }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!shippingInfo.full_name || !shippingInfo.email || !shippingInfo.address) {
      toast({ title: 'Missing Info', description: 'Please fill in all required shipping fields', variant: 'destructive' });
      return;
    }
    setCheckingOut(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/shop/checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
          shipping_info: shippingInfo,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Checkout failed');
      localStorage.removeItem('shop_cart');
      window.location.href = data.checkout_url;
    } catch (err) {
      toast({ title: 'Checkout Error', description: err.message, variant: 'destructive' });
    } finally {
      setCheckingOut(false);
    }
  };

  const sym = totals?.currency_symbol || 'CA$';

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <BackButton />
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <ShoppingBag className="w-20 h-20 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Your cart is empty</h2>
          <p className="text-gray-500 mb-6">Browse our shop and add items to your cart</p>
          <Link to="/shop" className="bg-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-pink-600 transition-colors">
            Browse Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <BackButton />
          <h1 className="text-xl font-bold text-gray-900">Your Cart ({cart.length} item{cart.length !== 1 ? 's' : ''})</h1>
          <div />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="lg:flex gap-6">
          {/* Cart Items */}
          <div className="lg:flex-1 space-y-3 mb-6 lg:mb-0">
            {cart.map((item) => (
              <div key={item.product_id} className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4" data-testid={`cart-item-${item.product_id}`}>
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm truncate">{item.name}</h3>
                  <p className="text-pink-600 font-bold text-sm">{sym}{parseFloat(item.price).toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => updateQty(item.product_id, -1)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 text-sm font-bold">−</button>
                  <span className="w-6 text-center text-sm font-semibold">{item.quantity}</span>
                  <button onClick={() => updateQty(item.product_id, 1)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 text-sm font-bold">+</button>
                </div>
                <button onClick={() => removeItem(item.product_id)} className="text-red-400 hover:text-red-600 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Checkout Panel */}
          <div className="lg:w-80">
            {/* Order Summary */}
            {loading ? (
              <div className="bg-white rounded-2xl p-6 shadow-sm flex items-center justify-center mb-4">
                <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
              </div>
            ) : totals && (
              <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
                <h3 className="font-bold text-gray-900 mb-4">Order Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span><span>{sym}{totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax ({totals.tax_rate}% HST)</span><span>{sym}{totals.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span>{totals.shipping === 0 ? <span className="text-green-600 font-medium">Free</span> : `${sym}${totals.shipping.toFixed(2)}`}</span>
                  </div>
                  {totals.shipping > 0 && (
                    <p className="text-xs text-gray-400">Free shipping on orders over {sym}{totals.free_shipping_threshold}</p>
                  )}
                  <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-gray-900 text-base">
                    <span>Total</span><span>{sym}{totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Shipping Form */}
            <form onSubmit={handleCheckout} className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Shipping Information</h3>
              <div className="space-y-3">
                {[
                  { key: 'full_name', label: 'Full Name *', type: 'text', required: true },
                  { key: 'email', label: 'Email *', type: 'email', required: true },
                  { key: 'phone', label: 'Phone', type: 'tel', required: false },
                  { key: 'address', label: 'Address *', type: 'text', required: true },
                  { key: 'city', label: 'City', type: 'text', required: false },
                  { key: 'province', label: 'Province', type: 'text', required: false },
                  { key: 'postal_code', label: 'Postal Code', type: 'text', required: false },
                ].map(({ key, label, type, required }) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                    <input
                      type={type}
                      required={required}
                      value={shippingInfo[key]}
                      onChange={(e) => setShippingInfo((s) => ({ ...s, [key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                      data-testid={`shipping-${key}`}
                    />
                  </div>
                ))}
              </div>
              <button
                type="submit"
                disabled={checkingOut || loading}
                className="w-full mt-4 bg-pink-500 hover:bg-pink-600 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                data-testid="checkout-btn"
              >
                {checkingOut ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : 'Proceed to Payment'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopCartPage;
